import { ProductListComponentBase } from '@utilities/product-list-component-base';
import { updateDocumentTitle, parseUrlParametersToActiveFilters, extractAndInjectStyles, cleanContentBlockElement, cleanupInjectedStyles, formatNostoErrorCode } from '@utilities/theme';
// Nosto utilities are lazy-loaded: a Shopify-only store never downloads them.
// fetchNostoData() (the only consumer) awaits this before use; the module is
// fetched once and cached by the browser/module system.
let nostoModulePromise = null;
function loadNostoUtilities() {
  if (!nostoModulePromise) {
    nostoModulePromise = import('@utilities/nosto');
  }
  return nostoModulePromise;
}

const setImageWidthParam = (imageUrl = "", width = 540) => {
  const rawImageUrl = String(imageUrl || "").trim();
  if (!rawImageUrl) return "";

  const hashIndex = rawImageUrl.indexOf("#");
  const hasHash = hashIndex !== -1;
  const hash = hasHash ? rawImageUrl.slice(hashIndex) : "";
  const urlWithoutHash = hasHash
    ? rawImageUrl.slice(0, hashIndex)
    : rawImageUrl;

  const queryIndex = urlWithoutHash.indexOf("?");
  const baseUrl =
    queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const queryString = queryIndex === -1 ? "" : urlWithoutHash.slice(queryIndex + 1);

  const params = new URLSearchParams(queryString);
  params.set("width", String(width));

  const nextQuery = params.toString();
  const querySuffix = nextQuery ? `?${nextQuery}` : "";

  return `${baseUrl}${querySuffix}${hash}`;
};

/**
 * ProductGrid - Unified custom element for managing product grids on search and collection pages
 *
 * This component handles both search results and collection product displays with comprehensive
 * filtering, sorting, and pagination support. It seamlessly integrates with both Shopify's
 * native search/collection APIs and Nosto's search platform.
 *
 * Responsibilities:
 * - Product data fetching from Shopify/Nosto APIs
 * - Product grid rendering and content block injection
 * - Pagination handling with browser history support
 * - URL state management for filters, sort, and pagination
 * - Event handling for filter changes (responds to filter-drawer events)
 * - Product-specific data transformations and lazy loading
 *
 * NOT Responsible For:
 * - Filter rendering or filter UI management (handled by FilterDrawer component)
 * - Filter metadata transformation for display (FilterDrawer transforms filter data)
 * - Filter option counts or availability (FilterDrawer fetches and manages this)
 *
 * Key Features:
 * - Dual data source support (Shopify/Nosto) with automatic transformation
 * - Real-time filtering and sorting with URL state management
 * - Server-side pagination with browser history support
 * - Content block injection for marketing content
 * - Lazy loading for performance optimization
 * - Filter drawer communication via events
 *
 * State Management:
 * - Maintains active filters, current page, and sort preferences
 * - Syncs state with URL parameters for shareable links
 * - Handles browser back/forward navigation
 * - Dispatches data events to notify FilterDrawer when product data includes filter metadata
 *
 * Data Flow:
 * 1. Initializes from URL parameters and configuration
 * 2. Fetches data from appropriate source (Shopify/Nosto)
 * 3. Transforms data to consistent format
 * 4. Renders products with templates
 * 5. Updates pagination and filter state
 * 6. Dispatches events for filter drawer synchronization
 *
 * @class
 * @extends HTMLElement
 * @customElement
 */
class ProductGrid extends ProductListComponentBase {
  /**
   * Creates an instance of ProductGrid
   * @constructor
   */
  constructor() {
    super();

    // Grid-specific configuration
    this.config.leverageUrl = true; // Default to true to maintain existing behavior

    // Grid-specific state properties
    this.state.currentPage = 1;
    this.state.activeFilters = {}; // Initialize as empty object

    // Grid-specific properties
    this.hasDispatchedInitialLoad = false; // First loadData is a view, not an update
    this.injectedStyles = new Set(); // Track injected style IDs to avoid duplicates
    this.cachedContentBlocks = null; // Cache processed content blocks per grid instance
    this.filterDebounceTimer = null; // Timer for debouncing filter changes
    this.deferredSlotObserver = null;

    // Late-subscriber handshake: cache the most recent payloads we broadcast so a
    // FilterDrawer / PaginationControls that upgrades AFTER our one-shot dispatch
    // (e.g. when the early-search primer makes the grid render before those
    // sibling modules attach their document listeners) can request and replay them.
    this.lastDispatchedFilterRequestDetail = null;
    this.lastDispatchedDataDetail = null;
    this.lastDispatchedPaginationDetail = null;

    // Bind event handlers to maintain context for proper cleanup
    this.boundHandlePopState = this.handlePopState.bind(this);
    this.boundHandleStateRequest = this.handleStateRequest.bind(this);
    this.boundHandleFilterChange = this.handleFilterChange.bind(this);
    this.boundHandleSortChange = this.handleSortChange.bind(this);
    this.boundHandleClearFilters = this.handleClearFilters.bind(this);
    this.boundHandleFacetsFilterChange = this.handleFacetsFilterChange.bind(this);
    this.boundHandlePaginationChange = this.handlePaginationChange.bind(this);

    // grid container
    this.container = this.closest("[data-product-grid-container]");
  }

  /**
   * Lifecycle callback when the element is added to the DOM
   * Either initializes the component immediately or sets up lazy loading
   * @returns {void}
   */
  async connectedCallback() {
    // Call base class connectedCallback first
    await super.connectedCallback();
  }

  /**
   * Lifecycle callback when the element is removed from the DOM
   * Cleans up observers, abort controllers, event listeners, and styles
   * @returns {void}
   */
  disconnectedCallback() {
    // Clear debounce timer on disconnect
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }

    // Clean up event listeners
    window.removeEventListener("popstate", this.boundHandlePopState);
    document.removeEventListener(
      "filter-drawer:filter-change",
      this.boundHandleFilterChange
    );
    document.removeEventListener(
      "filter-drawer:sort-change",
      this.boundHandleSortChange
    );
    document.removeEventListener(
      "filter-drawer:clear-filters",
      this.boundHandleClearFilters
    );
    document.removeEventListener(
      "facets:filters-changed",
      this.boundHandleFacetsFilterChange
    );
    document.removeEventListener(
      "pagination:change",
      this.boundHandlePaginationChange
    );
    document.removeEventListener(
      "product-grid:state-request",
      this.boundHandleStateRequest
    );

    this.teardownDeferredSlotObserver();

    // Clean up injected styles using utility function
    cleanupInjectedStyles(this.injectedStyles, { debug: this.allowDebug() });

    // Call base class disconnectedCallback last
    super.disconnectedCallback();
  }


  /**
   * Check if this is the main product grid on the page
   * Main grids control URL parameters and scroll to top of page
   * Non-main grids maintain independent state and scroll to their container
   * @returns {boolean}
   * @private
   */
  isMainGrid() {
    return this.dataset.mainProductGrid === "true";
  }

  /**
   * Flatten state.activeFilters into the standard events productFilters shape.
   *
   * activeFilters holds `{ paramName: [value, ...] }`, the same structure the URL
   * builder appends, so each value becomes one filter entry.
   * @returns {Array<{key: string, value: string}>}
   * @private
   */
  #buildProductFiltersPayload() {
    const activeFilters = this.state?.activeFilters;
    if (!activeFilters) return [];

    return Object.entries(activeFilters).flatMap(([key, values]) =>
      Array.isArray(values) ? values.map((value) => ({ key, value: String(value) })) : []
    );
  }

  /**
   * Dispatch shopify:collection:update or shopify:search:update for this grid.
   *
   * Called before the fetch so the event's promise represents the in-flight
   * update, which is what lets a listener show a loading state. The caller must
   * settle the returned handle on every path.
   *
   * Dispatched from loadData rather than from dispatchDataUpdateEvent, because that
   * method is replayed for late-connecting subscribers (handleStateRequest and
   * connectedCallback) and hooking it would emit duplicate standard events.
   *
   * Only the main grid reports: sliders and recommendation grids load products
   * without changing the collection or search the page represents.
   *
   * @returns {{resolve: Function, reject: Function}|null} Settlement handle, or
   *   null when this grid does not report updates
   * @private
   */
  #dispatchGridUpdateEvent() {
    if (!this.isMainGrid()) return null;

    const context = this.config?.context;
    if (context !== "collection" && context !== "search") return null;

    // The first load is a view, not an update. shopify:collection:view reports it
    // instead, so only filter, sort, and pagination changes dispatch here.
    if (!this.hasDispatchedInitialLoad) {
      this.hasDispatchedInitialLoad = true;
      return null;
    }

    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    // Nothing else awaits this promise, so an unhandled rejection would surface as
    // a console error on every superseded filter request.
    promise.catch(() => {});

    import("@shopify/events")
      .then(({ CollectionUpdateEvent, SearchUpdateEvent }) => {
        const sortKey = this.config?.sortBy || null;
        const productFilters = this.#buildProductFiltersPayload();

        if (context === "search") {
          this.dispatchEvent(
            new SearchUpdateEvent({
              search: {
                query: this.config?.terms || "",
                productFilters,
                sortKey,
              },
              promise: promise.then((data) => ({ totalCount: data?.resultsCount ?? 0 })),
            })
          );
          return;
        }

        this.dispatchEvent(
          new CollectionUpdateEvent({
            collection: {
              id: this.config.id ?? null,
              handle: this.config.handle || "",
              productsCount: this.state?.data?.resultsCount ?? 0,
            },
            productFilters,
            sortKey,
            promise: promise.then((data) => ({ productsCount: data?.resultsCount ?? 0 })),
          })
        );
      })
      .catch((error) => {
        if (this.allowDebug()) {
          console.warn("ProductGrid: failed to dispatch grid update standard event", error);
        }
      });

    return { resolve, reject };
  }

  /**
   * Dispatch Shopify's shopify:collection:view standard event from this grid.
   *
   * Only the main grid on a collection page reports a collection view. Sliders,
   * recommendations, and secondary grids render products without the page being a
   * view of their collection. Search grids are excluded because the standard event
   * for search results is shopify:search:update.
   *
   * Runs after the first loadData(), so productsCount reflects real results on both
   * the Nosto and Shopify paths, which converge on the same resultsCount key.
   * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
   * @returns {Promise<void>}
   * @private
   */
  async dispatchCollectionViewEvent() {
    if (!this.isMainGrid()) return;
    if (this.config.context !== "collection") return;

    try {
      const { CollectionViewEvent } = await import("@shopify/events");

      this.dispatchEvent(
        new CollectionViewEvent({
          collection: {
            id: this.config.id ?? null,
            handle: this.config.handle || "",
            productsCount: this.state.data?.resultsCount ?? 0,
          },
        })
      );
    } catch (error) {
      // Analytics dispatch must never break grid initialization.
      if (this.allowDebug()) {
        console.warn("ProductGrid: failed to dispatch shopify:collection:view", error);
      }
    }
  }

  /**
   * Scroll to the appropriate position based on grid type
   * Main grids scroll to top of page, non-main grids scroll to their container
   * @returns {void}
   * @private
   */
  scrollToGrid() {
    if (this.isMainGrid()) {
      // Main grid: scroll to top of page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      if (this.allowDebug()) {
        console.log("Scrolled to top of page (main grid)");
      }
    } else {
      // Non-main grid: scroll to the grid container
      if (this.container) {
        this.container.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        if (this.allowDebug()) {
          console.log("Scrolled to grid container (non-main grid)", this.container);
        }
      } else {
        if (this.allowDebug()) {
          console.log("Grid container not found, skipping scroll");
        }
      }
    }
  }

  /**
   * Initialize the component with configuration parsing, state setup, and data loading
   * Handles URL parameter parsing, filter initialization, and auto-loading logic
   * @returns {void}
   * @private
   */
  /**
   * Initialize ProductGrid with URL state, events, and auto-loading
   */
  async init() {
    try {
      // Initialize configuration and templates first.
      // We intentionally defer the first loadData() call until after URL state is applied
      // so direct visits like ?page=2 fetch/render the correct page on first paint.
      this.parseConfiguration();
      this.initTemplates();

      // ProductGrid-specific initialization
      // STRICT: Require gridId for proper event scoping
      if (!this.config.gridId) {
        console.error("ProductGrid: Missing required gridId (config.gridId). ProductGrid must have a gridId for proper event scoping.", {
          element: this,
          config: this.config,
          note: "gridId should be the section/block ID, not the collection ID"
        });
        // Still allow initialization but warn that events won't be scoped correctly
      }

      // Initialize state from URL parameters (this must happen after parseConfiguration)
      this.initStateFromUrl();

      // Set initial page from config if not already set from URL
      if (!this.state.currentPage && this.config.currentPage) {
        this.state.currentPage = this.config.currentPage;
      }

      // Initialize active filters from current URL parameters
      this.parseUrlParametersToActiveFilters();

      // Initialize event listeners (must happen after URL state initialization)
      this.initEvents();

      this.classList.add("product-grid--initialized");

      // First data load must happen after URL-derived state initialization.
      await this.loadData();

      await this.dispatchCollectionViewEvent();

      if (this.allowDebug()) {
        console.log("ProductGrid initialized", this);
      }
    } catch (error) {
      console.error("ProductGrid initialization error:", error);
      this.showErrorState("Failed to initialize product grid");
    }
  }

  /**
   * Set component state for CSS styling
   * @param {string} state
   */
  setComponentState(state) {
    this.setGridState(state);
  }

  /**
   * Get the container element for product cards
   * @returns {HTMLElement} Container element
   */
  getContainerElement() {
    return this.elements.productsGrid;
  }

  /**
   * Initialize DOM element references
   * @returns {void}
   * @private
   */
  initTemplates() {
    this.elements.templates = {};
    this.elements.errorState = this.querySelector("[data-error-state]");
    this.elements.emptyState = this.querySelector("[data-empty-state]");
    // Single persistent grid UL — SSR renders skeleton slots + content blocks here
    // and we hydrate slots in place (no separate loading-state / results-container).
    this.elements.productsGrid = this.querySelector("[data-products-grid]");
    this.elements.gridBody = this.querySelector("[data-products-grid-body]");
    this.elements.paginationContainer = this.querySelector(
      "[data-pagination-container]"
    );
    // Note: paginationControls element will be available after pagination snippet renders
    this.elements.paginationControls = null;

    // Product grid template elements only (filter templates are handled by filter-drawer)
    this.elements.templates.productCardComponent = this.querySelector(
      "template#product-card-component-template"
    );
    this.elements.templates.productCardInitialSkeleton = this.querySelector(
      "template#product-card-initial-skeleton-template"
    );
    this.elements.templates.contentBlocks = this.querySelector(
      "template#product-grid-template-content-blocks"
    );
    this.elements.templates.noResults = this.querySelector(
      "template#no-results-template"
    );

    // Additional elements for layout
    this.elements.gridTitle = this.querySelector("[data-grid-title]");
    this.elements.resultsCount = this.querySelector("[data-results-count]");
  }

  /**
   * Initialize event listeners
   * @returns {void}
   * @private
   */
  initEvents() {
    document.addEventListener(
      "filter-drawer:filter-change",
      this.boundHandleFilterChange
    );
    document.addEventListener(
      "filter-drawer:sort-change",
      this.boundHandleSortChange
    );
    document.addEventListener(
      "filter-drawer:clear-filters",
      this.boundHandleClearFilters
    );
    document.addEventListener(
      "facets:filters-changed",
      this.boundHandleFacetsFilterChange
    );
    document.addEventListener(
      "pagination:change",
      this.boundHandlePaginationChange
    );

    // Listen for browser back/forward button events (instance-specific)
    window.addEventListener("popstate", this.boundHandlePopState);

    // Listen for vendor changes from theme utilities (broadcasts to all grids, not identifier-based)
    document.addEventListener(
      "vendor:changed",
      this.handleVendorChange.bind(this)
    );

    // Listen for processing flag changes from theme utilities
    document.addEventListener(
      "processing:changed",
      this.handleProcessingChange.bind(this)
    );

    // Late-subscriber handshake: replay our latest broadcasts to a control that
    // connected after we already dispatched (closes the primer/init race).
    document.addEventListener(
      "product-grid:state-request",
      this.boundHandleStateRequest
    );

    // Notify filter drawer about our config when needed
    this.notifyFilterDrawer();
  }

  /**
   * Check if a FilterDrawer event is intended for this ProductGrid
   *
   * FilterDrawer identifier formats:
   * - Section context: "collection-filter-drawer-{section_id}"
   * - Block context: "{collection-id}-{section-id}-{block-id}"
   *
   * ProductGrid config.gridId is:
   * - Section context: section ID
   * - Block context: block ID
   *
   * Matching logic: Check if identifier ends with this grid's gridId (with or without separator)
   *
   * STRICT MODE: Requires identifier match - no fallback behavior
   *
   * @param {CustomEvent} event
   * @returns {boolean}
   * @private
   */
  shouldRespondToFilterDrawerEvent(event) {
    const eventIdentifier = event.detail?.identifier;

    // STRICT: Require identifier in event
    if (!eventIdentifier) {
      if (this.allowDebug()) {
        console.warn("ProductGrid: FilterDrawer event missing identifier - ignoring", event);
      }
      return false;
    }

    // STRICT: Require grid to have a gridId
    if (!this.config.gridId) {
      if (this.allowDebug()) {
        console.error("ProductGrid: Grid missing gridId (config.gridId) - cannot match events", {
          gridId: this.config.gridId,
          eventIdentifier,
          configId: this.config.id,
          note: "config.id is collection ID, config.gridId is section/block ID for event scoping"
        });
      }
      return false;
    }

    // Both identifiers are now handleized in Liquid, so we can compare directly
    // FilterDrawer identifier format: "collection-filter-drawer-{section-id}" or "{collection-id}-{section-id}-{block-id}"
    // ProductGrid gridId format: handleized section/block ID
    const gridId = String(this.config.gridId).toLowerCase();
    const identifierLower = String(eventIdentifier).toLowerCase();

    // Check if identifier ends with grid ID (with or without separator)
    // This works for both formats:
    // - "collection-filter-drawer-template-25787538407787__product-grid" ends with "template-25787538407787__product-grid" ✓
    // - "456-789-template-25787538407787__product-grid" ends with "template-25787538407787__product-grid" ✓
    const matches = identifierLower.endsWith(gridId) || identifierLower.endsWith(`-${gridId}`);

    if (!matches && this.allowDebug()) {
      console.log("ProductGrid: FilterDrawer event identifier mismatch - ignoring", {
        eventIdentifier,
        identifierLower,
        gridId,
        matches
      });
    }

    return matches;
  }

  /**
   * Notify filter drawer about our configuration
   * @private
   */
  notifyFilterDrawer() {
    if (!this.shouldAutoLoad()) return;
    const detail = {
      context: this.config.context,
      config: this.config,
      identifier: this.config.gridId,
    };
    this.lastDispatchedFilterRequestDetail = detail;
    const event = new CustomEvent("product-grid:filter-request", {
      detail,
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Late-subscriber handshake handler.
   *
   * A FilterDrawer / PaginationControls dispatches `product-grid:state-request`
   * (carrying its own identifier) when it finishes connecting. If it upgraded
   * after this grid already broadcast its one-shot events — the race the
   * early-search primer / faster CSR pipeline introduced — it would otherwise
   * never receive them. We replay our cached payloads so the late subscriber
   * catches up. Re-dispatched events carry this grid's identifier, so every
   * control still filters them via its own identifier matching.
   *
   * No-op until the grid has actually dispatched at least once; the normal
   * dispatch path covers the case where the control connected first.
   *
   * @param {CustomEvent} event - product-grid:state-request
   * @returns {void}
   * @private
   */
  handleStateRequest(event) {
    if (!this.matchesRequestIdentifier(event?.detail?.identifier)) return;

    if (this.lastDispatchedFilterRequestDetail) {
      document.dispatchEvent(
        new CustomEvent("product-grid:filter-request", {
          detail: this.lastDispatchedFilterRequestDetail,
          bubbles: true,
        })
      );
    }

    if (this.lastDispatchedDataDetail) {
      document.dispatchEvent(
        new CustomEvent("product-grid:data-updated", {
          detail: this.lastDispatchedDataDetail,
          bubbles: true,
        })
      );
    }

    if (this.lastDispatchedPaginationDetail) {
      document.dispatchEvent(
        new CustomEvent("product-grid:pagination-updated", {
          detail: this.lastDispatchedPaginationDetail,
          bubbles: true,
        })
      );
    }
  }

  /**
   * Decide whether a state-request targets this grid. Mirrors the permissive
   * identifier matching the controls use (exact, or either side ending with the
   * other) so simple section IDs and compound collection-section-block IDs both
   * resolve. A missing request identifier matches all grids (broadcast).
   *
   * @param {string} [requestIdentifier]
   * @returns {boolean}
   * @private
   */
  matchesRequestIdentifier(requestIdentifier) {
    if (!requestIdentifier) return true;
    if (!this.config.gridId) return false;

    const gridId = String(this.config.gridId).toLowerCase();
    const requested = String(requestIdentifier).toLowerCase();

    return (
      gridId === requested ||
      gridId.endsWith(`-${requested}`) ||
      gridId.endsWith(requested) ||
      requested.endsWith(`-${gridId}`) ||
      requested.endsWith(gridId)
    );
  }

  /**
   * Determine if component should auto-load data based on context and configuration
   * Search context requires terms, collection context requires handle or id
   * @returns {boolean} True if component should automatically load data
   * @private
   */
  shouldAutoLoad() {
    if (this.config.context === "search") {
      return this.config.terms && this.config.terms.trim().length > 0;
    } else if (this.config.context === "collection") {
      return this.config.handle || this.config.id;
    }
    return false;
  }

  /**
   * Load data from appropriate source (Shopify or Nosto) based on vendor configuration
   * Handles loading states, error handling, and data transformation
   * @returns {Promise<void>}
   */
  async loadData() {
    if (this.allowDebug()) {
      console.log("loadData", this);
    }
    if (this.state.isLoading) return;

    this.setState({ isLoading: true, hasError: false });
    this.showLoadingState();

    // Announce the pending update before fetching, so listeners can show a
    // loading state against the promise. Settled on every exit path below,
    // including the aborted one.
    const pendingUpdate = this.#dispatchGridUpdateEvent();

    // Hoisted so the catch can tell whether the failed fetch was a Nosto read (GC-2374 fallback).
    let vendor = "shopify";

    try {
      // Abort any existing request
      this.abortController?.abort();
      this.abortController = new AbortController();

      vendor = this.getDataVendor();
      let data;

      if (this.allowDebug()) {
        console.log("fetching data from", vendor);
      }

      if (vendor === "nosto") {
        data = await this.fetchNostoData();
      } else {
        data = await this.fetchShopifyData();
      }

      this.setState({ data, isLoading: false });

      pendingUpdate?.resolve(data);

      this.renderResults(data);
    } catch (error) {
      // A superseded request is a normal outcome, not a failure, but its promise
      // still has to settle or a listener waiting on it would hang forever.
      pendingUpdate?.reject(error);

      if (error.name === "AbortError") {
        return;
      }

      console.error("ProductGrid data loading error:", error);

      // Log technical details for debugging while showing user-friendly message
      if (this.allowDebug()) {
        console.error("ProductGrid technical error details:", {
          code: error.code,
          userMessage: error.userMessage,
          debugMessage: error.debugMessage,
          originalError: error.originalError,
          details: error.details
        });
      }

      // GC-2374: any Nosto failure that isn't "no results" (a zero-hit response resolves
      // successfully and never lands here) disables Nosto for the page and falls back to
      // Shopify. disableNostoForPage() fires a (deferred) forced vendor:changed that re-enters
      // loadData() on this grid, so we must clear isLoading first or the
      // `if (this.state.isLoading) return` guard would bail out the Shopify reload.
      if (vendor === "nosto" && window.theme?.utilities?.disableNostoForPage) {
        this.setState({ isLoading: false });
        window.theme.utilities.disableNostoForPage(formatNostoErrorCode(error) || error.message, error.code);
        return;
      }

      this.setState({ hasError: true, isLoading: false });

      // Use user-friendly error message if available, fallback to generic message
      const errorMessage = error.userMessage || error.message || "Unable to load products. Please try again.";

      // Compact diagnostic ref, surfaced on-screen so support screenshots carry
      // the failure mode (library/timeout/network/api), timing, and a lookup id.
      this.showErrorState(errorMessage, formatNostoErrorCode(error));
    }
  }

  /**
   * Consume the single-use early-search primer stashed by the inline PLP primer
   * script (see shopify/snippets/product-grid.liquid).
   *
   * The primer is deleted on first read regardless of vendor/query match, so all
   * reload paths (filters, sort, pagination, popstate, vendor:changed) naturally
   * bypass it. Only the main grid may read it — non-main grids must not steal or
   * delete the primer issued for the main grid.
   *
   * @param {string} vendor - Vendor this read is for ("nosto" | "shopify")
   * @returns {Object|null} The primer stash or null when absent/not applicable
   * @private
   */
  consumeEarlySearchPrimer(vendor) {
    if (!this.isMainGrid()) return null;

    const primer = window.__plpEarlySearch || null;
    if (!primer) return null;

    try {
      delete window.__plpEarlySearch;
    } catch (_error) {
      window.__plpEarlySearch = null;
    }

    if (primer.vendor !== vendor || !primer.promise) {
      if (this.allowDebug()) {
        console.warn("ProductGrid: early-search primer vendor mismatch - discarded", {
          primerVendor: primer.vendor,
          gridVendor: vendor,
        });
      }
      return null;
    }

    return primer;
  }

  /**
   * Fetch data from Shopify
   * @returns {Promise<Object>}
   * @private
   */
  async fetchShopifyData() {
    const url = this.buildShopifyUrl();

    // Early-search primer: consume the pre-issued request when the URL contract
    // matches exactly. Mismatch costs one duplicate request and is logged so
    // primer drift stays visible — fix the primer, never loosen the comparison.
    const primer = this.consumeEarlySearchPrimer("shopify");
    if (primer) {
      if (primer.url === url) {
        try {
          // Bound the wait: the primer fetch has no abort signal, so a stalled
          // request must not strand the grid — fall back to a fresh fetch.
          const data = await Promise.race([
            primer.promise,
            new Promise((_resolve, reject) => {
              setTimeout(
                () => reject(new Error("early-search primer timed out")),
                10000,
              );
            }),
          ]);
          if (this.allowDebug()) {
            console.log("ProductGrid: early-search primer consumed (shopify)", url);
          }
          return data;
        } catch (error) {
          if (this.allowDebug()) {
            console.warn("ProductGrid: early-search primer rejected, falling back (shopify)", error);
          }
        }
      } else if (this.allowDebug()) {
        console.warn("ProductGrid: early-search primer URL mismatch - issuing duplicate request (shopify)", {
          primerUrl: primer.url,
          gridUrl: url,
        });
      }
    }

    const response = await fetch(url, {
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();
    return JSON.parse(text);
  }

  /**
   * Fetch data from Nosto using the utilities system.
   *
   * Builds filters from active Shopify filters, constructs a Nosto query,
   * executes the search, and transforms the response to Shopify format.
   * Handles sort options with special logic for best-selling, manual, and relevance sorts.
   *
   * @returns {Promise<Object>} Shopify-compatible product data with pagination and filters
   * @throws {Error} When Nosto search fails
   * @private
   */
  async fetchNostoData() {
    try {
      const {
        nostoBuildQuery,
        nostoSearch,
        nostoTransformToShopifyProducts,
        nostoBuildFiltersFromShopify,
        nostoRecordSearchSubmit,
        nostoRecordAttribution,
        NOSTO_DEFAULT_TIMEOUT_MS,
        nostoLog,
      } = await loadNostoUtilities();

      nostoLog(`ProductGrid.fetchNostoData: START (context=${this.config.context})`);

      const filters = nostoBuildFiltersFromShopify(
        this.state.activeFilters,
        { debug: this.allowDebug() }
      );

      const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
      let nostoSortBy = this.config.sortBy || defaultSort;
      let shouldApplySort = true;

      if (nostoSortBy === "best-selling") {
        shouldApplySort = false;
        if (this.allowDebug()) {
          console.log("Nosto Wrapper: Not applying best-selling sort (no sort param sent)");
        }
      } else if (nostoSortBy === "manual") {
        shouldApplySort = false;
        if (this.allowDebug()) {
          console.log("Nosto Wrapper: Not applying manual (Featured) sort (no sort param sent)");
        }
      } else if (nostoSortBy === "relevance") {
        shouldApplySort = false;
        if (this.allowDebug()) {
          console.log("Nosto Wrapper: Not applying relevance sort (no sort param sent)");
        }
      }

      const queryParams = {
        context: this.config.context,
        terms: this.config.terms,
        categoryId: this.config.id,
        type: "products",
        size: this.config.settings.productsPerPage || 24,
        from:
          (this.state.currentPage - 1) *
          (this.config.settings.productsPerPage || 24),
        filters,
        allowSoldOutStateProducts: this.config.settings.allowSoldOutStateProducts || false,
      };

      // Only include sortBy if we should apply sort
      if (shouldApplySort) {
        queryParams.sortBy = nostoSortBy;
      }

      const queryConfig = nostoBuildQuery(queryParams);

      // Record search submit BEFORE the search request to mark as organic search
      // This is required for accurate Nosto analytics - see: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
      if (this.config.context === "search" && this.config.terms) {
        try {
          await nostoRecordSearchSubmit(this.config.terms, { debug: this.allowDebug() });
        } catch (e) {
          // Non-blocking - don't let tracking errors affect search functionality
          if (this.allowDebug()) {
            console.error('Nosto recordSearchSubmit error:', e);
          }
        }
      }

      // Early-search primer: consume the pre-issued search when query AND options
      // match exactly (JSON.stringify comparison; the inline primer mirrors
      // nostoBuildQuery key order). Mismatch costs one duplicate search and is
      // logged so primer drift stays visible — fix the primer, never loosen the
      // comparison. Rejection falls through to a normal nostoSearch.
      let response = null;
      const primer = this.consumeEarlySearchPrimer("nosto");
      if (primer) {
        const queryMatches =
          JSON.stringify(queryConfig.query) === JSON.stringify(primer.query);
        const optionsMatch =
          JSON.stringify(queryConfig.options) === JSON.stringify(primer.options);

        if (queryMatches && optionsMatch) {
          nostoLog(`ProductGrid: primer matched — racing primer.promise vs ${NOSTO_DEFAULT_TIMEOUT_MS}ms`);
          try {
            // The primer promise is a raw api.search() queued on the nostojs
            // command stub; it has none of nostoSearch's availability/timeout
            // safeguards. Bound the wait by the SINGLE global Nosto deadline
            // (NOSTO_DEFAULT_TIMEOUT_MS) so a library that never loads/drains the
            // queue cannot strand the grid past that deadline — on timeout we fall
            // back to nostoSearch, whose availability check shares the same singleton
            // deadline and so rejects immediately rather than waiting again.
            response = await Promise.race([
              primer.promise,
              new Promise((_resolve, reject) => {
                setTimeout(
                  () => reject(new Error("early-search primer timed out")),
                  NOSTO_DEFAULT_TIMEOUT_MS,
                );
              }),
            ]);
            nostoLog("ProductGrid: primer RESOLVED (library drained the queue)");
          } catch (error) {
            response = null;
            nostoLog(`ProductGrid: primer race lost/rejected → falling through to nostoSearch (${error.message})`);
          }
        } else if (this.allowDebug()) {
          console.warn("ProductGrid: early-search primer query mismatch - issuing duplicate search (nosto)", {
            queryMatches,
            optionsMatch,
            primerQuery: primer.query,
            gridQuery: queryConfig.query,
          });
        }
      }

      if (!response) {
        nostoLog("ProductGrid: no primer response → calling nostoSearch()");
        response = await nostoSearch(
          queryConfig.query,
          queryConfig.options,
          { debug: this.allowDebug(), trigger: 'product-grid' }
        );
      }

      // DUAL ATTRIBUTION: Record page-level attribution events for accurate Nosto dashboard reporting
      // IS (Internal Search) for search pages, VC (View Category) for collection pages
      // See: https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
      // Note: Fire-and-forget pattern to prevent blocking page load
      if (this.config.context === "search" && this.config.terms) {
        // Record Internal Search attribution (non-blocking)
        nostoRecordAttribution({
          type: "is", // Internal Search
          target: this.config.terms,
          refType: "os", // Onsite search
          debug: this.allowDebug()
          // timeout omitted → inherits the single NOSTO_DEFAULT_TIMEOUT_MS
        }).catch((e) => {
          // Silently handle attribution errors without blocking
          if (this.allowDebug()) {
            console.error('Nosto recordAttribution error:', e);
          }
        });
      } else if (this.config.context === "collection" && this.config.id) {
        // Record View Category attribution (non-blocking)
        nostoRecordAttribution({
          type: "vc", // View Category
          target: String(this.config.id),
          refType: "cmp", // Category merchandising
          debug: this.allowDebug()
          // timeout omitted → inherits the single NOSTO_DEFAULT_TIMEOUT_MS
        }).catch((e) => {
          // Silently handle attribution errors without blocking
          if (this.allowDebug()) {
            console.error('Nosto recordAttribution error:', e);
          }
        });
      }

      // Note: Search/category analytics are also handled by the `track` option passed to api.search()
      // via nostoBuildQuery. Additionally, we call recordSearchSubmit above for organic search marking.
      // Click and add-to-cart events are tracked via dual attribution in ProductCard.
      // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
      const hasActiveFilters = this.state.activeFilters &&
        Object.keys(this.state.activeFilters).length > 0 &&
        Object.values(this.state.activeFilters).some((values) =>
          Array.isArray(values) && values.length > 0,
        );
      const hasActiveSort =
        !!this.config.sortBy && this.config.sortBy !== defaultSort;
      const spotlightEnabled =
        this.config.context === "collection" &&
        !hasActiveFilters &&
        !hasActiveSort;

      const transformedData = nostoTransformToShopifyProducts(response, {
        context: this.config.context,
        terms: this.config.terms,
        currentPage: this.state.currentPage,
        pageSize: this.config.settings.productsPerPage || 24,
        sortBy: this.config.sortBy || defaultSort, // Keep original sort for URL/UI purposes
        spotlightConfig: {
          enabled: spotlightEnabled,
          colors: this.config.settings?.colorSpotlightColors || [],
          colorFamily: this.config.settings?.colorSpotlightColorFamily || "",
        },
        // Sale collections only. Applies in the default view and under the price
        // sorts, so it is deliberately NOT gated on hasActiveSort here — the
        // transform distinguishes price sorts from featured/alphabetical/newest.
        prioritizeSaleVariants:
          this.config.context === "collection" &&
          this.config.settings?.prioritizeSaleVariants === true,
        hasActiveSort,
        generatePaginationParts: this.generatePaginationParts.bind(this),
        getSortOptions: this.getSortOptions.bind(this)
      });
      return transformedData;
    } catch (error) {
      // Preserve the enhanced Nosto error metadata (code/stem/details) so the loadData()
      // fallback reports the real failure mode to datajet:nosto_failure (GC-2374) instead of
      // an empty code + bare message.
      const surfacedError = new Error(`Nosto search failed: ${error.message}`);
      surfacedError.code = error.code;
      surfacedError.stem = error.stem;
      surfacedError.userMessage = error.userMessage;
      surfacedError.debugMessage = error.debugMessage;
      surfacedError.details = error.details;
      throw surfacedError;
    }
  }

  /**
   * Build Shopify API URL based on context
   * @returns {string}
   * @private
   */
  buildShopifyUrl() {
    const baseUrl = window.location.origin;
    let path;
    const params = new URLSearchParams();

    if (this.config.context === "search") {
      path = window.theme.routes.search_url;
      if (this.config.terms) params.set("q", this.config.terms);
      if (this.config.type) params.set("type", this.config.type);
    } else if (this.config.context === "collection") {
      path = `${window.theme.routes.collections_url}/${this.config.handle}`;
    }

    // Add common parameters
    params.set("view", "csr-json");
    if (this.state.currentPage > 1) params.set("page", this.state.currentPage);
    if (this.config.settings.productsPerPage) params.set("products_per_page", this.config.settings.productsPerPage);

    // Add active filters
    this.addActiveFiltersToParams(params);

    // Add sort parameter
    if (this.config.sortBy) params.set("sort_by", this.config.sortBy);

    return `${baseUrl}${path}?${params.toString()}`;
  }

  /**
   * Add active filters to URL parameters (for Shopify)
   * @param {URLSearchParams} params
   * @private
   */
  addActiveFiltersToParams(params) {
    if (this.state.activeFilters && Object.keys(this.state.activeFilters).length > 0) {
      Object.entries(this.state.activeFilters).forEach(([key, values]) => {
        if(values && values.length > 0) {
          values.forEach((value) => {
            params.append(key, value);
          });
        }
      });
    }
  }

  /**
   * Generate pagination parts
   * @param {number} totalPages
   * @param {number} currentPage
   * @returns {Array}
   * @private
   */
  generatePaginationParts(totalPages, currentPage) {
    if (totalPages <= 1) return [];

    const parts = [];
    const showEllipsis = totalPages > 5;

    if (showEllipsis) {
      // Show first page
      parts.push({
        title: 1,
        url: this.buildPageUrl(1),
        isLink: currentPage !== 1,
      });

      if (currentPage > 3) {
        parts.push({
          title: "&hellip;",
          isLink: false,
        });
      }

      // Show current page and neighbors
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        parts.push({
          title: i,
          url: this.buildPageUrl(i),
          isLink: i !== currentPage,
        });
      }

      if (currentPage < totalPages - 2) {
        parts.push({
          title: "&hellip;",
          isLink: false,
        });
      }

      // Show last page
      if (totalPages > 1) {
        parts.push({
          title: totalPages,
          url: this.buildPageUrl(totalPages),
          isLink: currentPage !== totalPages,
        });
      }
    } else {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        parts.push({
          title: i,
          url: this.buildPageUrl(i),
          isLink: i !== currentPage,
        });
      }
    }

    return parts;
  }

  /**
   * Build URL for specific page
   * @param {number} page
   * @returns {string}
   * @private
   */
  buildPageUrl(page) {
    const currentUrl = new URL(window.location);
    const params = new URLSearchParams(currentUrl.search);

    if (page > 1) {
      params.set("page", page);
    } else {
      params.delete("page");
    }

    return `${currentUrl.pathname}?${params.toString()}`;
  }

  /**
   * Get sort options based on context
   * @returns {Array}
   * @private
   */
  getSortOptions() {
    if (this.config.context === "search") {
      // Search context: Only relevance and price sorting
      return [
        ["relevance", "Relevance"],
        ["price-ascending", "Price, low to high"],
        ["price-descending", "Price, high to low"],
      ];
    } else if (this.config.context === "collection") {
      // Collection context: Featured, best selling, and price sorting
      return [
        ["manual", "Featured"],
        ["best-selling", "Best Selling"],
        ["price-ascending", "Price, low to high"],
        ["price-descending", "Price, high to low"],
      ];
    }

    // Fallback for other contexts
    return [
      ["relevance", "Relevance"],
      ["price-ascending", "Price, low to high"],
      ["price-descending", "Price, high to low"],
    ];
  }

  /**
   * Render results to DOM
   * @param {Object} data
   * @private
   */
  renderResults(data) {
    if (this.allowDebug()) {
      console.log("rendering product grid with the given data", data);
    }

    // Set vendor data attribute from the rendering data
    this.dataset.vendor = data.vendor;

    if (!data.products || data.products.length === 0) {
      if(this.config.settings.hideOnEmptyProducts) {
        this.hideOnEmptyProducts();
      }
      this.showEmptyState();
      // Still dispatch event with empty data for filter drawer to update
      this.dispatchDataUpdateEvent(data);
      return;
    }

    // Set results state
    this.setGridState("results");

    // Render products into the single persistent grid UL.
    if (this.elements.productsGrid) {
      this.renderProducts(data.products, data.vendor);
    }

    // Render pagination using the pagination-controls component
    if (data.paginate) {
      this.renderPagination(data.paginate);
    }

    // Dispatch event to notify filter drawer of updated data
    this.dispatchDataUpdateEvent(data);
  }

  /**
   * Preload the first few product-card images as soon as the data is parsed.
   *
   * Only the LCP product-image case needs this (collections whose LCP is a media
   * content block are preloaded from SSR). Runs once, on the FIRST render only
   * (skipped on filter/sort/pagination re-renders, where the user has already
   * painted and a high-priority image preload would contend with the new grid).
   * Mirrors buildInjectedCardMarkup(): same setImageWidthParam(url) width=540.
   *
   * @param {Array} products
   * @private
   */
  preloadLcpProductImages(products) {
    // First render only (skip filter/sort/pagination re-renders — the user has
    // already painted and a high-priority preload would contend with the new grid).
    if (this._lcpProductImagesPreloaded || this._gridHydratedOnce) return;
    this._lcpProductImagesPreloaded = true;

    if (!Array.isArray(products) || products.length === 0) return;

    // Match the eager-card count the grid paints above the fold (first row or two).
    const PRELOAD_COUNT = 4;
    const head = document.head;
    if (!head) return;

    for (let i = 0; i < Math.min(PRELOAD_COUNT, products.length); i += 1) {
      // Match buildCardPayload's image source resolution exactly.
      const rawUrl = products[i]?.imageUrl || products[i]?.image?.src || "";
      const imageUrl = setImageWidthParam(rawUrl, 540);
      if (!imageUrl) continue;

      // Skip if something already requested this URL (e.g. SSR preload).
      if (head.querySelector(`link[rel="preload"][as="image"][href="${CSS.escape(imageUrl)}"]`)) {
        continue;
      }

      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = imageUrl;
      if (i === 0) link.setAttribute("fetchpriority", "high");
      link.dataset.role = "__product_lcp__";
      head.appendChild(link);
    }
  }

  /**
   * Render products grid
   * @param {Array} products
   * @private
   */
  renderProducts(products, vendor) {
    const grid = this.elements.productsGrid;
    if (!grid) return;

    // LCP fast-path: when the LCP element is a product image (collections with no
    // early media content block — the block case is preloaded server-side), the
    // image URL is unknown until now. Kick off the first few product-image fetches
    // immediately, in PARALLEL with the render below, so the LCP candidate's bytes
    // are arriving before its <img> is even injected.
    this.preloadLcpProductImages(products);

    // Fast path: FIRST render of a clean load. The SSR grid already holds skeleton
    // product slots interleaved with the real content blocks at their positions, so
    // we hydrate each slot IN PLACE — no clear/rebuild, content blocks never move.
    if (!this._gridHydratedOnce && this.canHydrateExistingGrid()) {
      this.hydrateExistingGrid(products, vendor);
      this._gridHydratedOnce = true;
      this.afterRenderProducts();
      return;
    }

    // Rebuild path: filter / sort / pagination / vendor re-render. Replace the grid
    // contents wholesale (skeleton slots + content blocks rebuilt from templates).
    this._gridHydratedOnce = true;
    this.rebuildGrid(products, vendor);
    this.afterRenderProducts();
  }

  /**
   * Whether the SSR grid can be hydrated in place: the persistent grid still holds
   * its untouched SSR skeleton slots (i.e. we haven't rebuilt yet). Pagination/
   * filter/sort re-renders go through rebuildGrid() instead.
   * @returns {boolean}
   * @private
   */
  canHydrateExistingGrid() {
    const grid = this.elements.productsGrid;
    if (!grid) return false;
    return grid.querySelector("[data-product-slot]") !== null;
  }

  /**
   * Hydrate the existing SSR skeleton slots into product cards IN PLACE, reconciling
   * the slot count against the returned products:
   *   - extra slots (more skeletons than products) are removed
   *   - missing slots (more products than skeletons) are appended at the end
   * Content blocks are SSR-rendered and left untouched.
   * @param {Array} products
   * @param {string} vendor
   * @private
   */
  hydrateExistingGrid(products, vendor) {
    const grid = this.elements.productsGrid;
    const slots = Array.from(grid.querySelectorAll("[data-product-slot]"));

    if (this.allowDebug()) {
      console.log(
        `Hydrating existing grid in place: ${slots.length} SSR slots, ${products.length} products`,
      );
    }

    // Trim excess skeleton slots FIRST — remove them before any hydration
    // so the user never sees cards pop in then vanish.
    if (slots.length > products.length) {
      for (let i = products.length; i < slots.length; i += 1) {
        slots[i].remove();
      }
    }

    const count = Math.min(slots.length, products.length);
    const limit = this.getEagerHydrationLimit();
    const deferred = [];
    for (let i = 0; i < count; i += 1) {
      const pendingSlot = { slotElement: slots[i], product: products[i], productArrayIndex: i };
      if (i < limit) {
        this.hydrateProductSlot(pendingSlot, vendor);
      } else {
        deferred.push(pendingSlot);
      }
    }
    if (deferred.length > 0) {
      this.hydrateProductSlotsDeferred(deferred, vendor);
    }

    // Append cards for products beyond the SSR slot count (more products than slots).
    if (products.length > slots.length) {
      const fragment = document.createDocumentFragment();
      for (let i = slots.length; i < products.length; i += 1) {
        const el = this.createProductElement(products[i], vendor, i);
        if (el) fragment.appendChild(el);
      }
      grid.appendChild(fragment);
    }
  }

  /**
   * Rebuild the grid contents from scratch (filter/sort/pagination/vendor change).
   * Clears the persistent grid UL and re-renders product slots interleaved with
   * content blocks (when shouldShowContentBlocks()), then hydrates them.
   * @param {Array} products
   * @param {string} vendor
   * @private
   */
  rebuildGrid(products, vendor) {
    const grid = this.elements.productsGrid;
    grid.replaceChildren();

    const fragment = document.createDocumentFragment();

    // Index content blocks by position (only when they should show on this view).
    let contentBlocksMap = null;
    if (this.shouldShowContentBlocks()) {
      contentBlocksMap = new Map();
      for (const block of this.getContentBlocks()) {
        if (!contentBlocksMap.has(block.index)) contentBlocksMap.set(block.index, []);
        contentBlocksMap.get(block.index).push(block);
      }
    }

    const renderSlots = [];
    for (let index = 0; index < products.length; index += 1) {
      const productIndex = index + 1;
      for (const contentBlock of contentBlocksMap?.get(productIndex) || []) {
        renderSlots.push({ type: "content", contentBlock });
      }
      renderSlots.push({ type: "product", product: products[index], productArrayIndex: index });
    }
    // Trailing content blocks positioned past the last product.
    if (contentBlocksMap) {
      const last = products.length;
      const trailing = Array.from(contentBlocksMap.keys()).filter(i => i > last).sort((a, b) => a - b);
      for (const index of trailing) {
        for (const contentBlock of contentBlocksMap.get(index) || []) {
          renderSlots.push({ type: "content", contentBlock });
        }
      }
    }

    // Build content blocks + product cards directly into the fragment.
    // Unlike the SSR path (hydrateExistingGrid) which arms existing skeletons
    // in place, the rebuild creates final <product-card> elements immediately —
    // the grid was just cleared so there's no CLS concern and no benefit to an
    // intermediate skeleton-then-observe step.
    for (const slot of renderSlots) {
      if (slot.type === "content") {
        fragment.appendChild(slot.contentBlock.element.cloneNode(true));
        continue;
      }
      const el = this.createProductElement(slot.product, vendor, slot.productArrayIndex);
      if (el) {
        fragment.appendChild(el);
      }
    }

    grid.appendChild(fragment);
  }

  /**
   * Shared post-render work (results count, grid title) for both render paths.
   * @private
   */
  afterRenderProducts() {
    // Update the banner search count and title if they exist
    if (
      this.elements.resultsCount &&
      this.state.data &&
      this.state.data.resultsCount !== undefined
    ) {
      this.elements.resultsCount.textContent = this.state.data.resultsCount;
    }

    // Update the grid title with results information
    if (this.elements.gridTitle && this.config.context === "search") {
      const resultsCount = this.state.data?.resultsCount || 0;
      const searchTerms = this.config.terms || "";

      if (resultsCount > 0) {
        this.elements.gridTitle.innerHTML = `<span class="search__count">${resultsCount}</span>&nbsp;results for "${searchTerms}"`;
      } else {
        this.elements.gridTitle.textContent = `No results found for "${searchTerms}"`;
      }
    }

    // Update document title for search context
    this.updateDocumentTitleProxy();

  }

  /**
   * Determine if content blocks should be shown
   * @returns {boolean}
   * @private
   */
  shouldShowContentBlocks() {
    if(!this.config.settings.showContentBlocks) {
      return false;
    }
    // Don't show content blocks if not on page 1
    if (this.state.currentPage !== 1) {
      if (this.allowDebug()) {
        console.log("Content blocks hidden: not on page 1");
      }
      return false;
    }

    // Don't show content blocks if there are active filters (excluding availability filters)
    if (this.hasNonAvailabilityActiveFilters()) {
      if (this.allowDebug()) {
        console.log("Content blocks hidden: active filters present");
      }
      return false;
    }

    return true;
  }

  /**
   * Check if there are active filters other than availability filters
   * @returns {boolean}
   * @private
   */
  hasNonAvailabilityActiveFilters() {
    const availabilityFilters = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    if (!this.state.activeFilters) {
      return false;
    }

    // Check if any filter is active that's not an availability filter
    for (const [filterKey, filterValues] of Object.entries(
      this.state.activeFilters
    )) {
      if (
        !availabilityFilters.includes(filterKey) &&
        Array.isArray(filterValues) &&
        filterValues.length > 0
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Extract content blocks from template and parse their positions
   * Caches results per grid instance to avoid re-processing
   * @returns {Array} Array of content block objects with index and element
   * @private
   */
  getContentBlocks() {
    // Return cached content blocks if available
    if (this.cachedContentBlocks !== null) {
      if (this.allowDebug()) {
        console.log("Using cached content blocks:", this.cachedContentBlocks.length);
      }
      return this.cachedContentBlocks;
    }

    const template = this.elements.templates.contentBlocks;
    if (!template) {
      if (this.allowDebug()) {
        console.log("No content blocks template found");
      }
      // Cache empty array to avoid re-checking
      this.cachedContentBlocks = [];
      return [];
    }

    const contentBlocks = [];
    const clone = template.content.cloneNode(true);

    // First, extract and inject any style tags
    extractAndInjectStyles(clone, this.injectedStyles, { debug: this.allowDebug() });

    // Get all direct children of the template content
    const children = Array.from(clone.children);

    children.forEach((child) => {
      // Look for various ways the position might be encoded
      let blockIndex = null;

      // Method 1: Check for data-position attribute (preferred method from template)
      if (child.hasAttribute("data-position")) {
        blockIndex = parseInt(child.getAttribute("data-position"), 10);
      }

      if (blockIndex && blockIndex > -1) {
        // Clean the element by removing any remaining style tags
        const cleanedElement = cleanContentBlockElement(
          child.cloneNode(true)
        );

        contentBlocks.push({
          index: blockIndex,
          element: cleanedElement,
        });

        if (this.allowDebug()) {
          console.log(`Content block found for index ${blockIndex}`);
        }
      } else {
        if (this.allowDebug()) {
          console.log(
            "Content block found but no valid index determined, skipping"
          );
        }
      }
    });

    // Sort content blocks by index to ensure proper order
    contentBlocks.sort((a, b) => a.index - b.index);

    if (this.allowDebug()) {
      console.log(`Total content blocks extracted: ${contentBlocks.length}`);
      contentBlocks.forEach((block) => {
        console.log(
          `- Block at index ${block.index}:`,
          block.element.tagName,
          block.element.className
        );
      });
    }

    // Cache the processed content blocks
    this.cachedContentBlocks = contentBlocks;
    return contentBlocks;
  }


  /**
   * Create product element from data
   * @param {Object} product
   * @param {string} vendor - The vendor of the product ("shopify" or "nosto")
   * @returns {HTMLElement|null}
   * @private
   */
  createProductElement(product, vendor, index = null) {
    if(this.allowDebug()) {
      console.log("createProductElement", { product, vendor, index });
    }
    // Try to use product-card component first
    const productCardElement = this.createProductCardComponent(product, vendor, index);
    if (productCardElement) {
      return productCardElement;
    }

    return null;
  }

  /**
   * Create product card component from data
   * @param {Object} product
   * @param {string} vendor - The vendor of the product ("shopify" or "nosto")
   * @returns {HTMLElement|null}
   * @private
   */
  createProductCardComponent(product, vendor, index = null) {
    const template = this.elements.templates.productCardComponent;
    if (!template) return null;

    const clone = template.content.cloneNode(true);
    const productCardElement = clone.querySelector("product-card");

    if (!productCardElement) return null;

    // Set up the product card component data attributes

    const defaultVariantId = product.defaultVariantId || null;
    const productUrl = `${product.url}${defaultVariantId ? `?variant=${defaultVariantId}` : ''}`;
    const fetchUrl = this.buildProductFetchUrl(product, defaultVariantId);

    // Generate unique component ID for heatmap tracking
    // Format: {context}-product-card-{product.id}
    // collection context is "collection-{collection.id}"
    // search context is "search-product-card-{product.id}"
    // fallback context is "product-grid"
    const componentId = this.config.context === "collection"
      ? `collection-${this.config.id}-product-card-${product.id}`
      : this.config.context === "search"
        ? `search-product-card-${product.id}`
        : `product-grid-product-card-${product.id}`;

    // gridId is still used for form ID scoping (functional, not heatmap)
    const sectionId = this.config.gridId || this.config.id || "product-grid";

    // Set data attributes for client-side rendering
    productCardElement.id = componentId;
    // productCardElement.classList.add("debug");
    productCardElement.dataset.productId = product.id; // Required for Nosto click tracking
    productCardElement.dataset.fetchUrl = fetchUrl;
    productCardElement.dataset.productUrl = productUrl;
    productCardElement.dataset.defaultVariantId = defaultVariantId;
    productCardElement.dataset.productFormId = `quick-add-${sectionId}${product.id}`;
    productCardElement.dataset.vendor = vendor;

    // Set Nosto context for attribution tracking
    if (vendor === "nosto") {
      const context = this.config.context === "search"
        ? "product-grid-search"
        : "product-grid-collection";
      productCardElement.dataset.nostoContext = context;
    }
    const eager = this.shouldUsePriorityFastPath(vendor, index, product);

    if (eager) {
      const cardPayload = this.buildCardPayload(
        { ...product, vendor },
        productUrl,
      );
      productCardElement.dataset.cardPayload = JSON.stringify(cardPayload);
      productCardElement.dataset.priorityMode = "true";
      productCardElement.dataset.priorityIndex = String(index);
      productCardElement.innerHTML = this.buildInjectedCardMarkup(cardPayload, {
        eager: true,
      });
    }

    productCardElement.classList.add("lazy-load");

    /**
     * Sold Out State Logic
     * This is extra post-processing logic. Ideally the product sourcing platform would handle and filter these out.
     * A product grid may show sold out products with a "Sold Out" state.
     * The product grid settings.allow_sold_out_state_products must be true.
     * The product must have the following tags:
     * - `shopify:unavailable`
     * - `greyson:available`
     * - `ignore-oos-logic`
     *
     * If all of the above are true, then the productCardElement will be given a dataset of dataset.showSoldOutState = true
     * If the product has the above tags, but the product grid settings.allow_sold_out_state_products is false, then the productCardElement will be skipped.
     */
    if (product.tags.includes("shopify:unavailable") && product.tags.includes("greyson:available") && product.tags.includes("ignore-oos-logic")) {
      if (this.config.settings.allowSoldOutStateProducts) {
        productCardElement.dataset.showSoldOutState = true;
      } else {
        return null;
      }
    }

    return clone;
  }

  /**
   * Build product fetch URL for client-side rendering
   * @param {Object} product
   * @returns {string}
   * @private
   */
  buildProductFetchUrl(product, defaultVariantId = null) {
    // For client-side rendering, we'll use the product URL with the product-json view parameter
    // Handle cases where the product URL already contains query parameters
    const url = new URL(product.url, window.location.origin);
    if (defaultVariantId) {
      url.searchParams.set("variant", defaultVariantId);
    }
    url.searchParams.set("view", "product-json");
    return url.toString();
  }

  isPriorityFastPathEnabled() {
    return true;
  }

  getEagerHydrationLimit() {
    return this.getPriorityFastPathLimit();
  }

  getPriorityFastPathLimit() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return 0;
    }
    return window.matchMedia("(max-width: 767px)").matches ? 2 : 4;
  }

  async hydrateProductSlot(pendingSlot, vendor) {
    const slot = pendingSlot.slotElement;
    const existingCard = slot.querySelector("product-card");

    // SSR skeleton path: arm the existing <product-card> in place — no DOM replacement.
    if (existingCard && !existingCard.dataset.fetchUrl) {
      // product-card.js may not have registered the custom element yet —
      // wait for the upgrade so arm() is available.
      if (typeof existingCard.arm !== "function") {
        await customElements.whenDefined("product-card");
        customElements.upgrade(existingCard);
      }

      const product = pendingSlot.product;
      const index = pendingSlot.productArrayIndex;
      const eager = this.shouldUsePriorityFastPath(vendor, index, product);

      let cardPayload = null;
      let injectedMarkup = null;
      if (eager) {
        const defaultVariantId = product.defaultVariantId || null;
        const productUrl = `${product.url}${defaultVariantId ? `?variant=${defaultVariantId}` : ""}`;
        cardPayload = this.buildCardPayload({ ...product, vendor }, productUrl);
        injectedMarkup = this.buildInjectedCardMarkup(cardPayload, { eager: true });
      }

      existingCard.arm({
        product,
        vendor,
        index,
        gridConfig: {
          context: this.config.context,
          id: this.config.id,
          gridId: this.config.gridId,
          settings: this.config.settings,
        },
        cardPayload,
        injectedMarkup,
      });

      // Clean up the placeholder styling
      slot.classList.remove("product-grid__item--placeholder");
      slot.removeAttribute("aria-hidden");
      return;
    }

    // Fallback: rebuild path or already-armed card — create and replace.
    const productElement = this.createProductElement(
      pendingSlot.product,
      vendor,
      pendingSlot.productArrayIndex,
    );

    if (productElement) {
      pendingSlot.slotElement.replaceWith(productElement);
      return;
    }

    pendingSlot.slotElement.remove();
  }

  hydrateProductSlotsDeferred(pendingProductSlots, vendor) {
    if (!pendingProductSlots || pendingProductSlots.length === 0) return;

    this.teardownDeferredSlotObserver();

    const slotDataMap = new WeakMap();
    for (const pendingSlot of pendingProductSlots) {
      slotDataMap.set(pendingSlot.slotElement, pendingSlot);
    }

    this.deferredSlotObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.deferredSlotObserver.unobserve(entry.target);
          const pendingSlot = slotDataMap.get(entry.target);
          if (pendingSlot) {
            slotDataMap.delete(entry.target);
            this.hydrateProductSlot(pendingSlot, vendor);
          }
        }
      },
      { rootMargin: "200px 0px" },
    );

    for (const pendingSlot of pendingProductSlots) {
      this.deferredSlotObserver.observe(pendingSlot.slotElement);
    }
  }

  teardownDeferredSlotObserver() {
    if (this.deferredSlotObserver) {
      this.deferredSlotObserver.disconnect();
      this.deferredSlotObserver = null;
    }
  }

  shouldUsePriorityFastPath(vendor, index, product = null) {
    // Decides whether this card's injected image loads eagerly with
    // fetchpriority=high (above-the-fold LCP candidates). Both vendors carry an
    // imageUrl in the list payload; an image is required for an eager shell.
    if (vendor !== "nosto" && vendor !== "shopify") return false;
    if (!(product?.imageUrl || product?.image?.src)) return false;
    if (!Number.isInteger(index) || index < 0) return false;
    if (!this.isPriorityFastPathEnabled()) return false;
    return index < this.getPriorityFastPathLimit();
  }

  /**
   * Build the vendor-agnostic CardPayload used to inject a near-complete,
   * visible product card up front (render-then-fetch). A strict superset of the
   * old priority payload: carries image+srcset, price markup, and up to 3
   * swatches so the card paints fully before the deferred product-json fetch.
   *
   * Both vendors converge on the same field names: the Shopify csr-json snippet
   * (product-results-products-json.liquid) and the Nosto transform
   * (nostoTransformToShopifyProducts) each emit imageSrcset/priceHtml/swatches/
   * color arrays. Missing fields (e.g. Nosto srcset/price_html) are null and the
   * deferred fetch fills them.
   *
   * @param {Object} product - Normalized product from either vendor
   * @param {string} productUrl - Product URL incl. ?variant=defaultVariantId
   * @returns {Object} CardPayload
   */
  buildCardPayload(product, productUrl) {
    const productId = product?.id || product?.productId || null;
    const title = product?.title || product?.name || "";
    const subtitle = product?.subtitle || "";
    const imageUrl = setImageWidthParam(
      product?.imageUrl || product?.image?.src || "",
    );
    const parsedPrice = Number(product?.price);
    const parsedCompareAt = Number(product?.compareAtPrice);

    const swatches = Array.isArray(product?.swatches)
      ? product.swatches.slice(0, 3).map((swatch) => ({
          option_value_id: swatch?.option_value_id ?? null,
          color: swatch?.color || swatch?.name || "",
          color_family: swatch?.color_family || "",
          handle: swatch?.handle || "",
          url: swatch?.url || productUrl,
          swatch_image: swatch?.swatch_image || "",
          swatch_image_aspect_ratio: swatch?.swatch_image_aspect_ratio ?? null,
          selected_image: swatch?.selected_image || "",
          available: swatch?.available !== false,
          on_sale: swatch?.on_sale === true,
          variant_id: swatch?.variant_id ?? null,
        }))
      : [];

    const payload = {
      productId,
      url: productUrl,
      defaultVariantId: product?.defaultVariantId ?? null,
      title,
      subtitle,
      image: {
        url: imageUrl,
        srcset: product?.imageSrcset || null,
        sizes: product?.imageSizes || null,
        alt: product?.imageAlt || title,
        width: 540,
        height: 720,
      },
      price: {
        price_html: product?.priceHtml || null,
        price_range_html: product?.priceRangeHtml || null,
        amount: Number.isFinite(parsedPrice) ? parsedPrice : null,
        compareAt: Number.isFinite(parsedCompareAt) ? parsedCompareAt : null,
        currency:
          product?.currency ||
          window?.Shopify?.currency?.active ||
          window?.Shopify?.currency?.iso_code ||
          "",
      },
      swatches,
      available_colors: Array.isArray(product?.availableColors)
        ? product.availableColors
        : [],
      sale_colors: Array.isArray(product?.saleColors)
        ? product.saleColors
        : [],
      hide_colors: Array.isArray(product?.hideColors)
        ? product.hideColors
        : [],
      vendor: product?.vendor || null,
    };

    // "complete" = enough to render the visible card without a fetch: an image
    // plus either price markup or a numeric price.
    payload.complete = Boolean(
      payload.image.url &&
        (payload.price.price_html || payload.price.amount !== null),
    );

    return payload;
  }

  buildSkeletonInitialMarkup() {
    const template = this.elements.templates.productCardInitialSkeleton;
    return (
      template?.innerHTML?.trim() ||
      `
        <div class="product-card__inner">
          <div class="product-card-skeleton__media">
            <div class="product-card-skeleton__media-image"></div>
          </div>
        </div>
        <div class="product-card__content">
          <div class="product-card__info product-card-skeleton__info">
            <div class="product-card-skeleton__title"></div>
            <div class="product-card-skeleton__subtitle"></div>
            <div class="product-card-skeleton__price"></div>
          </div>
          <div class="product-card-skeleton__swatches-row">
            <span class="product-card-skeleton__swatch"></span>
            <span class="product-card-skeleton__swatch"></span>
            <span class="product-card-skeleton__swatch"></span>
          </div>
        </div>
      `
    );
  }

  escapePriorityHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  /**
   * Build the full visible card body from a CardPayload (render-then-fetch).
   *
   * The markup is intentionally byte-compatible with what product-card.js's
   * renderClientSideCardTitle / renderClientSideCardPrice /
   * renderClientSideCardSwatches produce, so the deferred hydrate (which clears
   * the [data-slot] containers and re-renders) replaces like-for-like with zero
   * layout shift. Every card carries data-priority-shell="true" so the card's
   * hydrate-in-place branch (reuse the live <img>, no LCP reset) applies to all
   * cards, not just the eager ones.
   *
   * @param {Object} payload - CardPayload from buildCardPayload
   * @param {Object} [opts]
   * @param {boolean} [opts.eager] - Eager image + fetchpriority=high (priority cards)
   * @returns {string} HTML string for the <product-card> body
   */
  buildInjectedCardMarkup(payload, { eager = false } = {}) {
    if (!payload || !payload.complete) {
      return this.buildSkeletonInitialMarkup();
    }

    const esc = (value) => this.escapePriorityHtml(value);
    const url = esc(payload.url || "");
    const imageUrl = esc(setImageWidthParam(payload.image?.url || ""));
    if (!url || !imageUrl) return this.buildSkeletonInitialMarkup();

    const title = esc(payload.title || "");
    const subtitle = esc(payload.subtitle || "");
    const imageAlt = esc(payload.image?.alt || payload.title || "");
    const loading = eager ? "eager" : "lazy";
    const fetchPriority = eager ? ' fetchpriority="high"' : "";
    // srcset/sizes only when present (Shopify); Nosto upgrades on hydrate.
    const srcsetAttr = payload.image?.srcset
      ? ` srcset="${esc(payload.image.srcset)}"`
      : "";
    const sizesAttr = payload.image?.sizes
      ? ` sizes="${esc(payload.image.sizes)}"`
      : "";

    const mediaHtml = `
      <div class="product-card__media">
        <a class="product-card__media-link plp-card-primary-media" href="${url}" aria-label="${title}" role="presentation" aria-hidden="true" tabindex="-1">
          <img src="${imageUrl}" alt="${imageAlt}" width="540" height="720" loading="${loading}" decoding="async"${fetchPriority}${srcsetAttr}${sizesAttr} style="width:100%;display:block;">
        </a>
      </div>
      <div data-slot="media-badges"></div>
    `;

    const subtitleHtml = subtitle
      ? `<div data-slot="subtitle"><p class="product-card__subtitle">${subtitle}</p></div>`
      : `<div data-slot="subtitle"></div>`;

    const priceHtml = this.buildInjectedPriceMarkup(payload.price);
    const swatchesHtml = this.buildInjectedSwatchesMarkup(payload);

    return `
      <div class="product-card__inner" data-priority-shell="true">
        ${mediaHtml}
      </div>
      <div class="product-card__content">
        <div class="product-card__info">
          <div data-slot="info-badges"></div>
          <div data-slot="title">
            <h3 class="product-card__title">
              <a href="${url}" class="product-card__title-link">${title || "Product card"}</a>
            </h3>
          </div>
          ${subtitleHtml}
          <div data-slot="price">${priceHtml}</div>
        </div>
        <div data-slot="swatches">${swatchesHtml}</div>
      </div>
    `;
  }

  /**
   * Build the price block, matching renderClientSideCardPrice's wrapper chain so
   * the hydrate re-render is dimensionally identical. Prefers server price
   * markup; falls back to a single formatted price (Nosto) or a skeleton.
   *
   * @param {Object} price - CardPayload.price
   * @returns {string}
   */
  buildInjectedPriceMarkup(price) {
    if (!price) {
      return `<div class="product-card__price"><div class="product-card-skeleton__price"></div></div>`;
    }

    let optionHtml = "";
    let rangeHtml = "";

    if (price.price_html) {
      optionHtml = `<div class="product-card__price-option">${price.price_html}</div>`;
      rangeHtml = price.price_range_html
        ? `<div class="product-card__price-range">${price.price_range_html}</div>`
        : "";
    } else if (price.amount !== null && price.amount !== undefined) {
      const formatted = this.formatInjectedPrice(price.amount, price.currency);
      optionHtml = `<div class="product-card__price-option"><div class="price"> <span class="price-item"> <span><ins>${formatted}</ins> </span> </span> </div></div>`;
    } else {
      return `<div class="product-card__price"><div class="product-card-skeleton__price"></div></div>`;
    }

    return `<div class="product-card__price">${optionHtml}${rangeHtml}</div>`;
  }

  /**
   * Lightweight money formatter for the Nosto preview price only (a single
   * whole/decimal amount in dollars). The deferred fetch replaces this with the
   * server's exact price_html, so this only needs to be visually close.
   *
   * @param {number} amount - Price in dollars
   * @param {string} currency - ISO currency (informational)
   * @returns {string}
   */
  formatInjectedPrice(amount, currency) {
    const value = Number(amount);
    if (!Number.isFinite(value)) return "";
    const symbol = currency && currency !== "USD" ? "" : "$";
    const hasCents = Math.round(value * 100) % 100 !== 0;
    const body = hasCents ? value.toFixed(2) : String(Math.round(value));
    return `${symbol}${body}`;
  }

  /**
   * Build up to 3 swatch <li> matching renderClientSideCardSwatches markup, plus
   * a "More+" link when there are more available colors than rendered swatches.
   * The default/selected swatch is `checked`. Returns "" when no swatches (the
   * skeleton swatch row in the slot reserves height until then).
   *
   * @param {Object} payload - CardPayload
   * @returns {string}
   */
  buildInjectedSwatchesMarkup(payload) {
    const esc = (value) => this.escapePriorityHtml(value);
    const swatches = Array.isArray(payload?.swatches)
      ? payload.swatches.filter((swatch) => swatch && swatch.available !== false)
      : [];

    if (swatches.length === 0) {
      return `<div aria-hidden="true" class="product-card-skeleton__swatches-row"><span class="product-card-skeleton__swatch"></span><span class="product-card-skeleton__swatch"></span><span class="product-card-skeleton__swatch"></span></div>`;
    }

    const productHandle = esc(payload.productId || "");
    const handleSeed =
      esc(this.handleizeForId(payload.title)) || productHandle;

    let html = `<div class="product-card-swatches__container"><ul class="product-card-swatches product-card__option product-card__options--color" data-option="Color" data-option-key="option1">`;

    // First available swatch is the default-selected one in the preview.
    swatches.forEach((swatch, swatchIndex) => {
      const swatchHandle = esc(swatch.handle || "");
      const color = esc(swatch.color || "");
      const optionValueId = esc(swatch.option_value_id || "");
      const swatchImage = esc(swatch.swatch_image || "");
      const aspect = swatch.swatch_image_aspect_ratio ?? 1;
      const selectedImage = esc(swatch.selected_image || "");
      const checked = swatchIndex === 0 ? " checked" : "";
      html += `<li style="--swatch-image: url('${swatchImage}');--swatch-image-aspect-ratio: ${aspect};"><div class="product-card-swatch swatch--${swatchHandle}"><input type="radio" id="product-${handleSeed}-color-${swatchHandle}" name="color[${handleSeed}]" value="${color}" class="product__option-input product__option-swatch" data-color="${swatchHandle}" data-option-key="option1" data-option-value-id="${optionValueId}" data-selected-image="${selectedImage}"${checked}><label for="product-${handleSeed}-color-${swatchHandle}" class="product__option-label"><span class="visually-hidden">${color}</span></label></div></li>`;
    });

    const availableColorCount = Array.isArray(payload.available_colors)
      ? payload.available_colors.length
      : swatches.length;
    if (availableColorCount > swatches.length) {
      const moreLabel =
        window?.theme?.language_strings?.product_swatches_more_colors || "More+";
      html += `<li class="product-card-swatches-link-more"><a href="${esc(payload.url || "")}" class="link product-card__more-colors-link">${esc(moreLabel)}</a></li>`;
    }

    html += `</ul></div>`;
    return html;
  }

  /**
   * Handleize a string for use in DOM ids (mirrors the theme handleize used by
   * product-card.js so injected swatch ids match the hydrated ones).
   * @param {string} value
   * @returns {string}
   */
  handleizeForId(value) {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * Render pagination using the pagination-controls component
   * Dispatches event-based updates to ALL PaginationControls associated with this ProductGrid
   * Uses identifier-based event scoping (not DOM-based) to allow PaginationControl anywhere in DOM
   * @param {Object} paginate
   * @private
   */
  renderPagination(paginate) {
    if (!paginate) return;

    // STRICT: Require gridId for proper event scoping
    if (!this.config.gridId) {
      if (this.allowDebug()) {
        console.warn("ProductGrid: Cannot dispatch pagination update - missing gridId", {
          gridId: this.config.gridId,
          configId: this.config.id
        });
      }
      return;
    }

    // Build base URL without query parameters for pagination
    let baseUrl = window.location.pathname;

    // For collection context, use collection URL
    if (this.config.context === "collection" && this.config.url) {
      baseUrl = this.config.url;
    }

    if (this.allowDebug()) {
      console.log(
        "Dispatching pagination update event:",
        paginate,
        "baseUrl:",
        baseUrl,
        "isMainGrid:",
        this.isMainGrid(),
        "gridId:",
        this.config.gridId
      );
    }

    // Dispatch event for PaginationControls to listen to (event-based, not direct DOM queries)
    // All PaginationControls with matching identifier will receive and process this event
    const detail = {
      paginate,
      baseUrl,
      identifier: this.config.gridId,
      isMainGrid: this.isMainGrid()
    };
    this.lastDispatchedPaginationDetail = detail;
    const event = new CustomEvent("product-grid:pagination-updated", {
      detail,
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Update component state
   * @param {Object} newState
   * @private
   */
  setState(newState) {
    this.state = { ...this.state, ...newState };
  }

  /**
   * Show loading state
   * @private
   */
  showLoadingState() {
    this.setGridState("loading");
  }

  /**
   * Show error state
   * @param {string} message
   * @private
   */
  showErrorState(message, ref = "") {
    this.setGridState("error");
    if (this.elements.errorState) {
      const messageElement = this.elements.errorState.querySelector(
        "[data-error-message]"
      );
      if (messageElement) {
        messageElement.textContent =
          message || "An error occurred while loading products.";
      }

      // Render the compact diagnostic ref so it appears in support screenshots.
      // Hidden (empty) when there's no code, e.g. non-Nosto init failures.
      const refElement = this.elements.errorState.querySelector(
        "[data-error-ref]"
      );
      if (refElement) {
        refElement.textContent = ref || "";
        refElement.hidden = !ref;
      }
    }
  }

  /**
   * Show empty state
   * @private
   */
  showEmptyState() {
    this.setGridState("empty");
    // Update document title for empty state
    this.updateDocumentTitleProxy();
  }

  hideOnEmptyProducts() {
    this.classList.add("hidden");
    if(this.container) this.container.classList.add("hidden");
  }

  /**
   * Set grid state using data-state attribute
   * @param {string} state - 'loading', 'error', 'empty', 'results', or 'initial'
   * @private
   */
  setGridState(state) {
    // Set state using data attribute
    this.dataset.state = state;

    if (this.allowDebug()) {
      console.log("Grid state changed to:", state);
    }
  }

  /**
   * Update document title using theme utilities
   * @private
   */
  updateDocumentTitleProxy() {
    // Only compile title for search context
    if (this.config.context !== "search") {
      return null;
    }

    const searchTerms = this.config.terms || "";
    const resultsCount = this.state.data?.resultsCount || 0;

    let newTitle = "Search";

    if (searchTerms) {
      newTitle = `Search: ${resultsCount} results found for "${searchTerms}"`;
    }

    if (newTitle) {
      updateDocumentTitle(newTitle);

      if (this.allowDebug()) {
        console.log("Updated document title:", document.title);
      }
    }
  }

  /**
   * Check if grid is filtered or sorted (price sorting)
   * @returns {boolean} True if filters are active or price sorting is active
   * @private
   */
  isFilteredOrSorted() {
    // Check for active filters
    const hasActiveFilters = this.state.activeFilters &&
      Object.keys(this.state.activeFilters).length > 0 &&
      Object.values(this.state.activeFilters).some(values =>
        Array.isArray(values) && values.length > 0
      );

    // Check for price sorting only when it differs from the collection's default sort.
    // A collection whose defaultSortBy is already price-descending should not be treated
    // as "sorted" just because sortBy matches that default — doing so would wrongly suppress
    // sale-variant prioritization on those collections.
    const defaultSort = this.config.defaultSortBy || (this.config.context === 'collection' ? 'manual' : 'relevance');
    const isPriceSorted = (this.config.sortBy === 'price-ascending' || this.config.sortBy === 'price-descending') &&
                          this.config.sortBy !== defaultSort;

    return hasActiveFilters || isPriceSorted;
  }

  /**
   * Update filtered state on the DOM element
   * Sets data-filtered attribute and product-grid--filtered class based on filtered/sorted state
   * @private
   */
  updateFilteredState() {
    const isFiltered = this.isFilteredOrSorted();

    if (isFiltered) {
      this.setAttribute('data-filtered', 'true');
      this.classList.add('product-grid--filtered');
    } else {
      this.removeAttribute('data-filtered');
      this.classList.remove('product-grid--filtered');
    }

    if (this.allowDebug()) {
      console.log('ProductGrid: Updated filtered state:', isFiltered);
    }
  }

  /**
   * Handle filter change events from FilterDrawer
   * Debounced to prevent rapid re-renders
   * @param {CustomEvent} event
   * @private
   */
  async handleFilterChange(event) {
    // Check if this event is intended for this ProductGrid
    if (!this.shouldRespondToFilterDrawerEvent(event)) {
      if (this.allowDebug()) {
        console.log("handleFilterChange: Ignoring event - not for this grid", {
          eventIdentifier: event.detail?.identifier,
          gridId: this.config.id
        });
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("handleFilterChange", event.detail);
    }

    const { filterName, filterValue, action } = event.detail;

    // Ensure activeFilters is initialized
    if (!this.state.activeFilters) {
      this.state.activeFilters = {};
    }

    if (!this.state.activeFilters[filterName]) {
      this.state.activeFilters[filterName] = [];
    }

    if (action === "add") {
      if (!this.state.activeFilters[filterName].includes(filterValue)) {
        this.state.activeFilters[filterName].push(filterValue);
      }
    } else if (action === "remove") {
      this.state.activeFilters[filterName] = this.state.activeFilters[
        filterName
      ].filter((value) => value !== filterValue);

      // Remove empty filter arrays
      if (this.state.activeFilters[filterName].length === 0) {
        this.state.activeFilters[filterName] = undefined;
      }
    }

    if (this.allowDebug()) {
      console.log("Updated activeFilters:", this.state.activeFilters);
    }

    // Update filtered state
    this.updateFilteredState();

    // Reset to first page when filters change
    this.state.currentPage = 1;

    // Debounce filter changes to prevent rapid re-renders
    // Clear any existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer (300ms debounce - same as predictive search)
    this.filterDebounceTimer = setTimeout(async () => {
      // Update URL history before loading data
      this.updateUrlHistory();

      await this.loadData();

      // Clear timer reference
      this.filterDebounceTimer = null;
    }, 300);
  }

  /**
   * Handle pagination change events
   * Main grids update URL history, non-main grids only update internal state
   * @param {CustomEvent} event
   * @private
   */
  async handlePaginationChange(event) {
    if (this.allowDebug()) {
      console.log("handlePaginationChange", event);
    }

    // STRICT: Check if this event is intended for this ProductGrid
    const eventIdentifier = event.detail?.identifier;
    if (eventIdentifier) {
      // If event has identifier, verify it matches this grid
      if (!this.config.gridId) {
        if (this.allowDebug()) {
          console.error("ProductGrid: Pagination event has identifier but grid missing config.gridId - ignoring", {
            eventIdentifier,
            gridId: this.config.gridId,
            configId: this.config.id,
            note: "config.id is collection ID, config.gridId is section/block ID for event scoping"
          });
        }
        return;
      }

      const gridId = String(this.config.gridId).toLowerCase();
      const identifierLower = String(eventIdentifier).toLowerCase();

      // Match if identifier exactly equals gridId or ends with gridId (with or without separator)
      // This handles both simple identifiers (section ID) and compound identifiers (collection-section-block)
      const matches = identifierLower === gridId || identifierLower.endsWith(`-${gridId}`) || identifierLower.endsWith(gridId);

      if (!matches) {
        if (this.allowDebug()) {
          console.log("ProductGrid: Pagination event identifier mismatch - ignoring", {
            eventIdentifier,
            gridId,
            matches
          });
        }
        return;
      }
    } else {
      // STRICT: Require identifier in pagination events
      if (this.allowDebug()) {
        console.warn("ProductGrid: Pagination event missing identifier - ignoring", event);
      }
      return;
    }

    const { page } = event.detail;
    this.state.currentPage = page;

    // update the data-page attribute
    this.dataset.page = page;

    // update the data-products-container attribute
    const productContainer = this.querySelector("[data-products-container]");
    if(productContainer) {
      productContainer.dataset.page = page;
    }

    // Only update URL history if this is the main grid
    if (this.isMainGrid()) {
      this.updateUrlHistory();
      if (this.allowDebug()) {
        console.log("Updated URL history (main grid)");
      }
    } else {
      if (this.allowDebug()) {
        console.log("Skipped URL history update (non-main grid)");
      }
    }

    // Scroll to appropriate position based on grid type
    this.scrollToGrid();

    await this.loadData();
  }

  /**
   * Handle sort change events from FilterDrawer
   * Debounced to prevent rapid re-renders
   * @param {CustomEvent} event
   * @private
   */
  async handleSortChange(event) {
    // Check if this event is intended for this ProductGrid
    if (!this.shouldRespondToFilterDrawerEvent(event)) {
      if (this.allowDebug()) {
        console.log("handleSortChange: Ignoring event - not for this grid", {
          eventIdentifier: event.detail?.identifier,
          gridId: this.config.id
        });
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("handleSortChange", event);
    }
    const { sortBy } = event.detail;
    this.config.sortBy = sortBy;
    this.state.currentPage = 1; // Reset to first page

    // Debounce sort changes to prevent rapid re-renders
    // Clear any existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer (300ms debounce)
    this.filterDebounceTimer = setTimeout(async () => {
      // Update URL history before loading data
      this.updateUrlHistory();

      await this.loadData();

      // Clear timer reference
      this.filterDebounceTimer = null;
    }, 300);
  }

  /**
   * Handle clear filters events from FilterDrawer
   * Debounced to prevent rapid re-renders
   * @param {CustomEvent} event
   * @private
   */
  async handleClearFilters(event) {
    // Check if this event is intended for this ProductGrid
    if (!this.shouldRespondToFilterDrawerEvent(event)) {
      if (this.allowDebug()) {
        console.log("handleClearFilters: Ignoring event - not for this grid", {
          eventIdentifier: event.detail?.identifier,
          gridId: this.config.id
        });
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("handleClearFilters", event);
    }
    this.state.activeFilters = {};
    this.state.currentPage = 1;

    // Debounce clear filters to prevent rapid re-renders
    // Clear any existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer (300ms debounce)
    this.filterDebounceTimer = setTimeout(async () => {
      // Update URL history before loading data
      this.updateUrlHistory();

      await this.loadData();

      // Clear timer reference
      this.filterDebounceTimer = null;
    }, 300);
  }

  /**
   * Handle vendor change events from theme utilities
   * @param {CustomEvent} event
   * @private
   */
  async handleVendorChange(event) {
    const { vendor, forced } = event.detail || {};

    // Context-vendor grids (collection PLP pinned to a fixed vendor) normally ignore vendor
    // changes. The exception is the GC-2374 forced fallback: when Nosto has failed, `forced`
    // reloads this grid from Shopify too (getDataVendor() returns 'shopify' because
    // theme.data.nostoDisabled is set). Without this carve-out the main collection grid would
    // stay on the loading skeleton after a Nosto failure.
    if (this.hasContextVendor() && forced !== true) {
      return;
    }

    if (this.allowDebug()) {
      console.log("handleVendorChange", event);
      console.log(`ProductGrid: Search vendor changed to ${vendor}${forced ? ' (forced)' : ''}, reloading data...`);
    }

    // Reset to first page when vendor changes
    this.state.currentPage = 1;

    // Reload data with new vendor
    await this.loadData();
  }

  /**
   * Handle processing flag changes from theme utilities
   * @param {CustomEvent} event
   * @private
   */
  async handleProcessingChange(event) {
    if (this.allowDebug()) {
      console.log("handleProcessingChange", event);
    }

    const { enabled } = event.detail;

    if (this.allowDebug()) {
      console.log(`ProductGrid: Product processing ${enabled ? 'enabled' : 'disabled'}, reloading data...`);
    }

    // Reset to first page when processing changes
    this.state.currentPage = 1;

    // Reload data with new processing logic
    await this.loadData();
  }

  /**
   * Handle filter changes from facets.js for compatibility
   * @param {CustomEvent} event
   * @private
   */
  async handleFacetsFilterChange(event) {
    if (this.allowDebug()) {
      console.log("handleFacetsFilterChange", event.detail);
    }

    const { urlParameters } = event.detail;

    // Parse URL parameters and update activeFilters
    this.parseUrlParametersToActiveFilters(urlParameters);

    // Update filtered state (parseUrlParametersToActiveFilters already calls updateFilteredState, but ensure it's set)
    this.updateFilteredState();

    // Reset to first page when filters change
    this.state.currentPage = 1;

    // Update URL history before loading data
    this.updateUrlHistory();

    await this.loadData();
  }

  /**
   * Parse URL parameters and update activeFilters state
   * @param {string|null} urlParameters - URL search parameters string, or null to use current location
   * @private
   */
  parseUrlParametersToActiveFilters(urlParameters = null) {
    // Only read from URL if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      this.state.activeFilters = {};
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, not reading filters from URL");
      }
      return;
    }

    // Use shared utility function to parse URL parameters
    this.state.activeFilters = parseUrlParametersToActiveFilters(urlParameters, {
      debug: this.allowDebug()
    });

    // Update filtered state after parsing URL parameters
    this.updateFilteredState();
  }

  /**
   * Initialize component state from URL parameters
   * @private
   */
  initStateFromUrl() {
    // Only read from URL if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, not initializing state from URL");
      }
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    // Initialize pagination from URL
    const pageParam = urlParams.get("page");
    if (pageParam) {
      this.state.currentPage = parseInt(pageParam, 10) || 1;
    }

    // Initialize sort from URL
    const sortParam = urlParams.get("sort_by");
    if (sortParam) {
      this.config.sortBy = sortParam;
    } else if (!this.config.sortBy && this.config.defaultSortBy) {
      // Use default_sort_by from config if no URL param and no existing sort_by
      this.config.sortBy = this.config.defaultSortBy;
    }

    // Initialize search terms from URL (for search context)
    if (this.config.context === "search") {
      const searchTerms = urlParams.get("q");
      if (searchTerms) {
        this.config.terms = searchTerms;
      }

      const searchType = urlParams.get("type");
      if (searchType) {
        this.config.type = searchType;
      }
    }

    if (this.allowDebug()) {
      console.log("Initialized state from URL:", {
        currentPage: this.state.currentPage,
        sortBy: this.config.sortBy,
        terms: this.config.terms,
        type: this.config.type,
      });
    }

    // Update filtered state after initializing from URL
    this.updateFilteredState();
  }


  /**
   * Public method to refresh data
   * @returns {Promise<void>}
   */
  async refresh() {
    if (this.allowDebug()) {
      console.log("refresh", this);
    }
    await this.loadData();
  }

  /**
   * Public method to update search terms (for search context)
   * @param {string} terms
   * @returns {Promise<void>}
   */
  async updateSearchTerms(terms) {
    if (this.config.context !== "search") return;

    this.config.terms = terms;
    this.state.currentPage = 1;
    this.state.activeFilters = {};

    if (terms && terms.trim().length > 0) {
      await this.loadData();
    } else {
      this.showEmptyState();
    }
  }

  /**
   * Public method to update collection (for collection context)
   * @param {string} handle
   * @returns {Promise<void>}
   */
  async updateCollection(handle) {
    if (this.config.context !== "collection") return;

    this.config.handle = handle;
    this.state.currentPage = 1;
    this.state.activeFilters = {};

    await this.loadData();
  }

  /**
   * Set collection title (useful for collection context)
   * @param {string} title
   */
  setCollectionTitle(title) {
    this.config.collectionTitle = title;
  }

  /**
   * Dispatch data update event to notify filter drawer
   * @param {Object} data - Updated data
   * @private
   */
  dispatchDataUpdateEvent(data) {
    if (this.allowDebug()) {
      console.log("Dispatching data update event:", data);
    }

    const detail = { data, identifier: this.config.gridId };
    this.lastDispatchedDataDetail = detail;
    const event = new CustomEvent("product-grid:data-updated", {
      detail,
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Update the browser URL history with current state
   * @private
   */
  updateUrlHistory() {
    // Skip URL updates if leverageUrl is disabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, skipping URL history update");
      }
      return;
    }

    try {
      const newUrl = this.buildCurrentStateUrl();
      const currentUrl = window.location.href;

      // Only update history if the URL has actually changed
      if (newUrl !== currentUrl) {
        // Use pushState to update URL without page reload
        // This allows back/forward button functionality
        window.history.pushState(
          {
            filters: this.state.activeFilters,
            page: this.state.currentPage,
            sortBy: this.config.sortBy,
            context: this.config.context,
            terms: this.config.terms,
          },
          "",
          newUrl
        );

        if (this.allowDebug()) {
          console.log("Updated URL history:", newUrl);
        }
      }
    } catch (error) {
      console.error("Failed to update URL history:", error);
    }
  }

  /**
   * Build URL that represents current component state
   * @returns {string}
   * @private
   */
  buildCurrentStateUrl() {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams();

    // Add search/collection specific parameters
    if (this.config.context === "search") {
      if (this.config.terms) {
        params.set("q", this.config.terms);
      }
      if (this.config.type) {
        params.set("type", this.config.type);
      }
    }

    // Add pagination
    if (this.state.currentPage && this.state.currentPage > 1) {
      params.set("page", this.state.currentPage);
    }

    // Add sort parameter
    const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
    if (this.config.sortBy && this.config.sortBy !== defaultSort) {
      params.set("sort_by", this.config.sortBy);
    }

    // Add active filters
    if (this.state.activeFilters) {
      Object.entries(this.state.activeFilters).forEach(([key, values]) => {
        if (Array.isArray(values)) {
          values.forEach((value) => {
            params.append(key, value);
          });
        }
      });
    }

    // Build the full URL
    currentUrl.search = params.toString();
    return currentUrl.toString();
  }

  /**
   * Handle browser back/forward button navigation
   * @param {PopStateEvent} event
   * @private
   */
  async handlePopState(event) {
    if (this.allowDebug()) {
      console.log("handlePopState", event);
    }

    // Only handle popstate if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, ignoring popstate event");
      }
      return;
    }

    // Re-parse URL parameters to update component state
    const urlParams = new URLSearchParams(window.location.search);

    // Update search terms if in search context
    if (this.config.context === "search") {
      const newTerms = urlParams.get("q") || "";
      if (newTerms !== this.config.terms) {
        this.config.terms = newTerms;
      }
    }

    // Update pagination
    const pageParam = urlParams.get("page");
    this.state.currentPage = pageParam ? parseInt(pageParam, 10) : 1;

    // Update sort
    const sortParam = urlParams.get("sort_by");
    const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
    this.config.sortBy = sortParam || defaultSort;

    // Update active filters from URL (this now properly resets filters)
    this.parseUrlParametersToActiveFilters();

    // Reload data with new state (don't update URL history again)
    await this.loadData();

    // Ensure FilterDrawer is notified of the state change (especially for empty filter states)
    if (this.state.data) {
      this.dispatchDataUpdateEvent(this.state.data);
    }
  }
}

customElements.define("product-grid", ProductGrid);
